/** @jsx jsx */
import { useEffect, useContext, Fragment } from 'react'
import { css, jsx } from '@emotion/core'
import { NotificationProvider, ToastConsumer } from '@livechat/design-system'

import { Context } from '../context'
import { Header, Nav } from '../components'
import { Sandbox, Feedback, Surveys } from '../views'
import { VIEWS } from '../constants'
import { useAuth } from '../hooks'
import api from '../api'
import animateIcon from '../assets/icons/animate-icon-lc.svg'
import surveysIcon from '../assets/icons/surveys-logo-lc.svg'
import errorIcon from '../assets/icons/error.svg'

import { StoreProvider } from '../store'
import useFetchSettings from '../hooks/useFetchSettings'

const { SANDBOX, FEEDBACK, SURVEYS } = VIEWS

const appCss = css`
  display: flex;
  width: 100vw;
  height: 100vh;

  @media (max-width: 855px) {
    flex-direction: column-reverse;
  }

  .loading {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    > img {
      width: 200px;
      margin-bottom: 100px;
    }
  }

  .sorry {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    > span {
      margin-bottom: 85px;
      font-size: 18px;

      display: flex;
      align-items: center;

      > img {
        margin-right: 3px;
        width: 20px;
      }
    }

    > img {
      width: 100px;
      margin-bottom: 10px;
    }
  }
`

const toastCss = css`
  z-index: 15000;
`

const App = () => {
  const {
    view,
    setSurveys,
    surveyToken,
    setSurveyToken,
    setSurveysLoading,
    hasAccess,
    setHasAccess,
    setSettings,
  } = useContext(Context)

  const { accessToken: livechatToken, isLoggingIn, agentId } = useAuth()

  useEffect(() => {
    const request = async () => {
      try {
        const data = await api.getSurveyToken(livechatToken, agentId)
        const { survey_token, hasAccess: has_access } = JSON.parse(data)

        setSurveyToken(survey_token)
        setHasAccess(has_access)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err)
      }
    }

    if (livechatToken && !surveyToken && agentId) {
      request()
    }
  }, [livechatToken])

  useEffect(() => {
    const request = async () => {
      setSurveysLoading(true)
      const data = await api.getSurveys(surveyToken)
      setSurveys(data)
      setSurveysLoading(false)
    }

    if (surveyToken && hasAccess) {
      request()
    }
  }, [surveyToken, hasAccess])

  useEffect(() => {
    const request = async () => {
      setSurveysLoading(true)
      const data = await api.getSurveys(surveyToken)
      setSurveys(data)
      setSurveysLoading(false)
    }

    if (surveyToken && hasAccess) {
      request()
    }
  }, [surveyToken, hasAccess])

  useFetchSettings({ surveyToken, setSettings })

  return (
    <div css={appCss}>
      {isLoggingIn || typeof hasAccess === 'undefined' ? (
        <div className="loading">
          <img src={animateIcon} alt="loading" />
        </div>
      ) : hasAccess ? (
        <Fragment>
          <Nav />
          <div>
            <Header />
            {view === SANDBOX && <Sandbox />}
            {view === SURVEYS && <Surveys />}
            {view === FEEDBACK && <Feedback />}
          </div>
        </Fragment>
      ) : (
        <div className="sorry">
          <img src={surveysIcon} alt="loading" />
          <span>
            <img src={errorIcon} alt="error" />
            You do not have permission
          </span>
        </div>
      )}
    </div>
  )
}

const AppWithProviders = () => {
  return (
    <StoreProvider>
      <NotificationProvider>
        <ToastConsumer
          horizontalPosition="center"
          fixed
          verticalPosition="top"
          css={toastCss}
        />
        <App />
      </NotificationProvider>
    </StoreProvider>
  )
}

export default AppWithProviders
